import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Combobox } from './Combobox';
import { useDictado } from '../hooks/useDictado';

const OPCIONES_COMBUSTIBLE = [
  'Nafta', 'Diesel', 'Híbrido Nafta', 'Híbrido Diesel',
  'Híbrido Enchufable Nafta', 'Híbrido Enchufable Diesel', '100% eléctrico'
];
const OPCIONES_TRANSMISION = ['Manual', 'Automático'];
const OPCIONES_ADMISION = ['Aspirado', 'Turbo'];

export default function NuevoClienteForm({ onCreado, onCancelar }) {
  const [nuevoCliente, setNuevoCliente] = useState({ nombre: '', celular: '', rut: '', direccion: '' });
  const [nuevoVehiculo, setNuevoVehiculo] = useState({
    marca: '', modelo: '', anio: '', motor: '', matricula: '',
    n_chasis: '', n_motor: '', combustible: '', transmision: '', admision: '',
    notas_vehiculo: ''
  });
  const [marcasModelos, setMarcasModelos] = useState({});
  const [cilindradas, setCilindradas] = useState([]);
  const [anios, setAnios] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [puliendoNotas, setPuliendoNotas] = useState(false);

  const { grabando, soportado, iniciar, detener } = useDictado();

  useEffect(() => {
    api('/vehiculos/marcas-modelos/todos').then(setMarcasModelos).catch(() => {});
    api('/vehiculos/cilindradas/todas').then(setCilindradas).catch(() => {});
    api('/vehiculos/anios/todos').then(setAnios).catch(() => {});
  }, []);

  const marcasList = Object.keys(marcasModelos).sort();
  const modelosDeLaMarca = marcasModelos[nuevoVehiculo.marca] || [];

  const agregarMarcaNueva = (marca) => {
    setNuevoVehiculo(p => ({ ...p, marca }));
    api(`/vehiculos/marcas-modelos/agregar?marca=${encodeURIComponent(marca)}&modelo=${encodeURIComponent(nuevoVehiculo.modelo || '(sin modelo)')}`, { method: 'POST' });
    setMarcasModelos(m => ({ ...m, [marca]: m[marca] || [] }));
  };

  const agregarModeloNuevo = (modelo) => {
    setNuevoVehiculo(p => ({ ...p, modelo }));
    if (nuevoVehiculo.marca) {
      api(`/vehiculos/marcas-modelos/agregar?marca=${encodeURIComponent(nuevoVehiculo.marca)}&modelo=${encodeURIComponent(modelo)}`, { method: 'POST' });
      setMarcasModelos(m => ({ ...m, [nuevoVehiculo.marca]: [...(m[nuevoVehiculo.marca] || []), modelo] }));
    }
  };

  const agregarCilindradaNueva = (valor) => {
    setNuevoVehiculo(p => ({ ...p, motor: valor }));
    api(`/vehiculos/cilindradas/agregar?valor=${encodeURIComponent(valor)}`, { method: 'POST' });
    setCilindradas(c => [...c, valor]);
  };

  const toggleDictadoNotas = () => {
    if (grabando) { detener(); return; }
    iniciar(async (textoCrudo) => {
      if (!textoCrudo) return;
      setPuliendoNotas(true);
      try {
        const res = await api('/importar/pulir-dictado', {
          method: 'POST', body: JSON.stringify({ texto_crudo: textoCrudo })
        });
        setNuevoVehiculo(p => ({ ...p, notas_vehiculo: p.notas_vehiculo ? `${p.notas_vehiculo}\n${res.texto}` : res.texto }));
      } catch {
        setNuevoVehiculo(p => ({ ...p, notas_vehiculo: p.notas_vehiculo ? `${p.notas_vehiculo}\n${textoCrudo}` : textoCrudo }));
      }
      setPuliendoNotas(false);
    });
  };

  const guardar = async () => {
    if (!nuevoCliente.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setGuardando(true); setError('');
    try {
      const cResult = await api('/clientes/', {
        method: 'POST',
        body: JSON.stringify(nuevoCliente)
      });
      const vResult = await api('/vehiculos/', {
        method: 'POST',
        body: JSON.stringify({ id_cliente: cResult.id, ...nuevoVehiculo })
      });
      const clienteCreado = { id: cResult.id, ...nuevoCliente };
      const vehiculoCreado = { id: vResult.id, ...nuevoVehiculo };
      onCreado(clienteCreado, vehiculoCreado);
    } catch (e) {
      setError(e.message || 'No se pudo guardar');
    }
    setGuardando(false);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.75rem', color: 'var(--azul)' }}>Datos del cliente</p>
        <div className="form-grid-2" style={{ marginBottom: '.75rem' }}>
          <div className="form-group">
            <label className="form-label">Nombre completo <span className="req">*</span></label>
            <input className="form-input" value={nuevoCliente.nombre}
              onChange={e => setNuevoCliente(p => ({ ...p, nombre: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Celular</label>
            <input className="form-input" value={nuevoCliente.celular}
              onChange={e => setNuevoCliente(p => ({ ...p, celular: e.target.value }))} />
          </div>
        </div>
        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">RUT o CI (opcional)</label>
            <input className="form-input" value={nuevoCliente.rut}
              onChange={e => setNuevoCliente(p => ({ ...p, rut: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Dirección</label>
            <input className="form-input" value={nuevoCliente.direccion}
              onChange={e => setNuevoCliente(p => ({ ...p, direccion: e.target.value }))} />
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.75rem', color: 'var(--azul)' }}>Datos del vehículo</p>

        <div className="form-grid-3" style={{ marginBottom: '.75rem' }}>
          <div className="form-group">
            <label className="form-label">Marca</label>
            <Combobox value={nuevoVehiculo.marca} options={marcasList}
              placeholder="Ej: Volkswagen" nombreEntidad="marcas"
              onChange={v => setNuevoVehiculo(p => ({ ...p, marca: v, modelo: '' }))}
              onNuevoValor={agregarMarcaNueva} />
          </div>
          <div className="form-group">
            <label className="form-label">Modelo</label>
            <Combobox value={nuevoVehiculo.modelo} options={modelosDeLaMarca}
              placeholder="Ej: Saveiro" nombreEntidad="modelos"
              onChange={v => setNuevoVehiculo(p => ({ ...p, modelo: v }))}
              onNuevoValor={agregarModeloNuevo} />
          </div>
          <div className="form-group">
            <label className="form-label">Año</label>
            <select className="form-select" value={nuevoVehiculo.anio}
              onChange={e => setNuevoVehiculo(p => ({ ...p, anio: e.target.value }))}>
              <option value="">Seleccionar...</option>
              {anios.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>

        <div className="form-grid-2" style={{ marginBottom: '.75rem' }}>
          <div className="form-group">
            <label className="form-label">Motor</label>
            <Combobox value={nuevoVehiculo.motor} options={cilindradas}
              placeholder="Ej: 1.6" nombreEntidad="motores"
              onChange={v => setNuevoVehiculo(p => ({ ...p, motor: v }))}
              onNuevoValor={agregarCilindradaNueva} />
          </div>
          <div className="form-group">
            <label className="form-label">Matrícula</label>
            <input className="form-input" value={nuevoVehiculo.matricula}
              onChange={e => setNuevoVehiculo(p => ({ ...p, matricula: e.target.value }))} />
          </div>
        </div>

        <div className="form-grid-3" style={{ marginBottom: '.75rem' }}>
          <div className="form-group">
            <label className="form-label">Admisión</label>
            <select className="form-select" value={nuevoVehiculo.admision}
              onChange={e => setNuevoVehiculo(p => ({ ...p, admision: e.target.value }))}>
              <option value="">Seleccionar...</option>
              {OPCIONES_ADMISION.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Combustible</label>
            <select className="form-select" value={nuevoVehiculo.combustible}
              onChange={e => setNuevoVehiculo(p => ({ ...p, combustible: e.target.value }))}>
              <option value="">Seleccionar...</option>
              {OPCIONES_COMBUSTIBLE.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Transmisión</label>
            <select className="form-select" value={nuevoVehiculo.transmision}
              onChange={e => setNuevoVehiculo(p => ({ ...p, transmision: e.target.value }))}>
              <option value="">Seleccionar...</option>
              {OPCIONES_TRANSMISION.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div className="form-grid-2" style={{ marginBottom: '.75rem' }}>
          <div className="form-group">
            <label className="form-label">N° Chasis</label>
            <input className="form-input" value={nuevoVehiculo.n_chasis}
              onChange={e => setNuevoVehiculo(p => ({ ...p, n_chasis: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">N° Motor</label>
            <input className="form-input" value={nuevoVehiculo.n_motor}
              onChange={e => setNuevoVehiculo(p => ({ ...p, n_motor: e.target.value }))} />
          </div>
        </div>

        <div className="form-group" style={{ marginTop: '.75rem' }}>
          <div className="sec-header" style={{ marginBottom: '.3rem' }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Notas del vehículo</label>
            {soportado && (
              <button type="button" className={`mic-btn ${grabando ? 'recording' : ''}`}
                onClick={toggleDictadoNotas} title={grabando ? 'Detener dictado' : 'Dictar por voz'}>
                🎤
              </button>
            )}
          </div>
          {grabando && <p style={{ color: 'var(--rojo)', fontSize: '.8125rem', marginBottom: '.4rem' }}>🔴 Escuchando... tocá el micrófono de nuevo para terminar</p>}
          {puliendoNotas && <p style={{ fontSize: '.8125rem', color: 'var(--gris-4)', marginBottom: '.4rem' }}>Redactando...</p>}
          <textarea className="form-textarea" rows={2} value={nuevoVehiculo.notas_vehiculo}
            onChange={e => setNuevoVehiculo(p => ({ ...p, notas_vehiculo: e.target.value }))} />
        </div>
      </div>

      {error && <p style={{ color: 'var(--rojo)', fontSize: '.875rem', marginBottom: '.75rem' }}>{error}</p>}

      <div style={{ display: 'flex', gap: '.5rem' }}>
        {onCancelar && <button className="btn btn-secondary" onClick={onCancelar} disabled={guardando}>← Cancelar</button>}
        <button className="btn btn-primary" style={{ flex: 1 }} disabled={guardando} onClick={guardar}>
          {guardando ? 'Guardando...' : 'Guardar cliente'}
        </button>
      </div>
    </div>
  );
}
